/**
 * Format a number as Indian currency with Lakhs/Crores display
 * @param {number} amount - Amount to format
 * @returns {string} Formatted amount with Indian currency symbol
 */
export const formatIndianCurrency = (amount) => {
  if (!amount && amount !== 0) return '₹0';
  
  const numericAmount = parseFloat(amount);
  if (isNaN(numericAmount)) return '₹0';

  if (numericAmount < 100000) {
    return `₹${Math.round(numericAmount).toLocaleString('en-IN')}`;
  }
  
  // 100 lakhs = 10,000,000
  const hundredLakhs = 10000000;
  
  if (numericAmount >= hundredLakhs) {
    // Convert to crores and show with 2 decimal places
    const crores = numericAmount / 10000000;
    return `₹${crores.toFixed(2)} Cr`;
  } else {
    // Convert to lakhs and keep one decimal for more precise smaller pipeline values
    const lakhs = numericAmount / 100000;
    return `₹${lakhs.toFixed(lakhs >= 10 ? 0 : 1)} Lakhs`;
  }
};

export const getCurrencySymbol = (priceUom = '') => {
  const normalized = String(priceUom || '').toUpperCase();
  if (normalized.includes('EUR')) return '€';
  if (normalized.includes('USD')) return '$';
  if (normalized.includes('GBP')) return '£';
  return '₹';
};

export const formatCurrencyWithSymbol = (amount, priceUom = '') => {
  const normalized = String(priceUom || '').toUpperCase();
  const symbol = getCurrencySymbol(priceUom);
  if (!amount && amount !== 0) return `${symbol}0`;
  const numericAmount = parseFloat(amount);
  if (isNaN(numericAmount)) return `${symbol}0`;

  const hasForeignCurrency = normalized.includes('USD') || normalized.includes('EUR') || normalized.includes('GBP');
  const isInr = (normalized.includes('INR') && !hasForeignCurrency) || (!hasForeignCurrency && symbol === '₹');
  if (!isInr) {
    const formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: numericAmount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    });
    return `${symbol}${formatter.format(numericAmount)}`;
  }

  if (numericAmount < 100000) {
    return `${symbol}${Math.round(numericAmount).toLocaleString('en-IN')}`;
  }

  const hundredLakhs = 10000000;
  if (numericAmount >= hundredLakhs) {
    const crores = numericAmount / 10000000;
    return `${symbol}${crores.toFixed(2)} Cr`;
  }

  const lakhs = numericAmount / 100000;
  return `${symbol}${lakhs.toFixed(lakhs >= 10 ? 0 : 1)} Lakhs`;
};
  
/**
 * Parse and validate deal value
 * @param {any} value - Value to parse
 * @returns {number} Parsed numeric value or 0
 */
export const parseDealValue = (value) => {
  if (!value && value !== 0) return 0;
  
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
};
  
/**
 * Format date in Indian format
 * @param {string} dateString - Date string to format
 * @returns {string} Formatted date
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'No date set';
  try {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return 'Invalid date';
  }
};
